import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  DeliveryFeeCreateRequest,
  deliveryFeeCreateSchema,
  deliveryFeeUpdateSchema,
  type DeliveryFee,
  type DeliveryFeeCreateFormValues,
  type DeliveryFeeUpdateFormValues,
} from "@/types/delivery-fee-type";
import { createDeliveryFee, updateDeliveryFee } from "@/api/delivery-fee-api";
import { useToast } from "@/hooks/use-toast";
import { SaveButton } from "@/components/ui/buttons/save-button";

type FormMode = "create" | "update";

interface DeliveryFeeFormProps {
  mode: FormMode;
  initialValues?: Partial<DeliveryFee> & { id?: string };
  onSuccess?: (saved: DeliveryFee) => void;
  onCancel?: () => void;
}

const deliveryTypeOptions = [
  { value: "Normal_In_City", label: "Normal In City" },
  { value: "Outside_City", label: "Outside City" },
  { value: "Express", label: "Express" },
];

export function DeliveryFeeForm({
  mode,
  initialValues,
  onSuccess,
  onCancel,
}: DeliveryFeeFormProps) {
  const { toast } = useToast();

  const isCreate = mode === "create";

  const resolver = zodResolver(
    isCreate ? deliveryFeeCreateSchema : deliveryFeeUpdateSchema
  );

  const {
    control,
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<DeliveryFeeCreateFormValues | DeliveryFeeUpdateFormValues>({
    resolver,
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      deliveryType: initialValues?.deliveryType ?? deliveryTypeOptions[0].value,
      name: initialValues?.name ?? "",
      description: initialValues?.description ?? "",
      price: initialValues?.price ?? 0,
      isActive: initialValues?.isActive ?? true,
    },
  });

  const onSubmit: SubmitHandler<
    DeliveryFeeCreateFormValues | DeliveryFeeUpdateFormValues
  > = async (values) => {
    try {
      let saved: any;
      if (isCreate) {
        saved = await createDeliveryFee(values as DeliveryFeeCreateRequest);
      } else {
        const id = (initialValues as any)?.id as string | undefined;
        if (!id) {
          throw new Error("Missing id for update");
        }
        saved = await updateDeliveryFee(
          id,
          values as DeliveryFeeUpdateFormValues
        );
      }

      toast({
        title: isCreate ? "Created successfully" : "Updated successfully",
      });
      onSuccess?.(saved?.data ?? saved);
    } catch (error: any) {
      toast({
        title: "Operation failed",
        description: error?.message ?? undefined,
        variant: "destructive",
      });
    }
  };

  // Lightweight section wrapper for consistent spacing and headings
  const FormSection = React.useCallback(
    ({
      title,
      description,
      children,
    }: {
      title: string;
      description?: string;
      children: React.ReactNode;
    }) => (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: description ? 0.5 : 1 }}>
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {description}
          </Typography>
        ) : null}
        {children}
      </Box>
    ),
    []
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <FormSection title="Basic Information">
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12} md={6} sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Name"
                      placeholder="e.g. Standard Delivery"
                      InputLabelProps={{ shrink: true }}
                      {...register("name" as any)}
                      error={!!errors.name}
                      helperText={errors.name?.message as string}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ mb: 2 }}>
                    <FormControl fullWidth error={!!errors.deliveryType}>
                      <InputLabel id="delivery-type-label">
                        Delivery Type
                      </InputLabel>
                      <Controller
                        name={"deliveryType" as any}
                        control={control}
                        render={({ field }) => (
                          <Select
                            labelId="delivery-type-label"
                            label="Delivery Type"
                            value={(field.value as string) ?? ""}
                            onChange={field.onChange}
                          >
                            {deliveryTypeOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {errors.deliveryType?.message && (
                        <FormHelperText>
                          {errors.deliveryType.message as string}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ mb: 2 }}>
                    <Controller
                      name={"price" as any}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          label="Price"
                          placeholder="e.g. 5.00"
                          type="number"
                          inputMode="decimal"
                          inputProps={{ step: "0.01" }}
                          InputLabelProps={{ shrink: true }}
                          value={field.value ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === "" ? 0 : Number(value));
                          }}
                          error={!!errors.price}
                          helperText={errors.price?.message as string}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Controller
                          name={"isActive" as any}
                          control={control}
                          render={({ field }) => (
                            <Switch
                              checked={field.value as boolean}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      placeholder="e.g. Standard delivery within city limits"
                      multiline
                      rows={3}
                      InputLabelProps={{ shrink: true }}
                      {...register("description" as any)}
                      error={!!errors.description}
                      helperText={errors.description?.message as string}
                    />
                  </Grid>
                </Grid>
              </FormSection>

              <Divider sx={{ my: 2 }} />

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                  mt: 1,
                }}
              >
                <Button
                  onClick={onCancel}
                  variant="outlined"
                  color="inherit"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <SaveButton
                  disabled={isSubmitting}
                  handleSave={handleSubmit(onSubmit)}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </form>
  );
}

export default DeliveryFeeForm;
